/**
 * Fix & Flip PDF Generator
 * Handles PDF generation specifically for Fix & Flip strategy
 */

import {
  expensesSection,
  revenuesSection,
  graphSection,
  getFixFlipChartData,
} from '../utils/pdfUtils'
import { currencyFormat } from '@/utils/number'
import {
  sum,
  map,
  zip,
  compact,
} from '@/utils/lodash'
import { BasePDFGenerator } from './BasePDFGenerator'
import { buildProformaDescriptionPdfNode, buildProformaDisclaimerPdfNode } from '@/utils/propertyDescription'

export class FixFlipPDFGenerator extends BasePDFGenerator {
  async generatePDF() {
    return this.generateTwoPageDocDefinition()
  }

  /**
   * Generate page 1 lower right section
   */
  async generatePage1LowerRight() {
    const { property } = this
    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      acquisition = {},
      assumptions = {},
    } = property

    const netROI = acquisition.analytics.fixFlip.netROIFixFlip
      ? (acquisition.analytics.fixFlip.netROIFixFlip * 100).toFixed(2)
      : 0

    const saleProceeds = acquisition.analytics.fixFlip.saleProceeds
      ? acquisition.analytics.fixFlip.saleProceeds
      : 0

    const percentageOfARV = acquisition.analytics.fixFlip
      .percentageOfARV
      ? acquisition.analytics.fixFlip.percentageOfARV * 100
      : 0

    const sqftString = sum(sqft)
      ? sum(sqft).toLocaleString('en')
      : '0'

    return [
      {
        text: ['Financial ', { text: 'Returns', color: '#3399ff' }],
        fontSize: 18,
        margin: [0, 5, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 365,
            h: 92,
            r: 2,
          },
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 365,
            h: 92,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 360 },
        stack: [
          {
            text: ['Fix & Flip'],
            fontSize: 16,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 381 },
        stack: [
          {
            text: [
              'Net Profit:   ',
              {
                text: currencyFormat.format(saleProceeds),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 397 },
        stack: [
          {
            text: [
              'Net ROI:  ',
              {
                text: netROI + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 413 },
        stack: [
          {
            text: [
              'All-In Cost to ARV:   ',
              {
                text: percentageOfARV.toFixed(2) + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      // {
      //   absolutePosition: { x: 220, y: 429 },
      //   stack: [
      //     {
      //       text: [
      //         'Equity:   ',
      //         {
      //           text: currencyFormat.format(equity),
      //           fontSize: 14,
      //           bold: true,
      //         },
      //       ],
      //       fontSize: 13,
      //       alignment: 'left',
      //     },
      //   ],
      // },

      {
        text: [
          'Property ',
          { text: 'Information', color: '#3399ff' },
        ],
        fontSize: 18,
        margin: [0, 20, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
        ],
      },
      {
        columns: [
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Beds:   ',
                  { text: sum(beds), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Baths:   ',
                  { text: sum(baths), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 140,
          },
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Sq Feet:   ',
                  {
                    text: sqftString,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Year:   ',
                  {
                    text: property.year,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 'auto',
          },
        ],
      },
      buildProformaDescriptionPdfNode(property),
    ]
  }

  /**
   * Generate page 2 top section
   */
  async generatePage2Top() {
    const { property } = this
    const { acquisition = {}, assumptions = {} } = property

    const mortgagePayment = acquisition.analytics.fixFlip
      .mortgageFixFlip
      ? acquisition.analytics.fixFlip.mortgageFixFlip
      : 0
    const financeAmount = acquisition.analytics.fixFlip
      .loanAmountFixFlip
      ? acquisition.analytics.fixFlip.loanAmountFixFlip
      : 0
    const downPaymentTotal = acquisition.analytics.fixFlip
      .downPaymentTotalFixFlip
      ? acquisition.analytics.fixFlip.downPaymentTotalFixFlip
      : 0
    const pointsAmount = acquisition.analytics.fixFlip
      .pointsAmountFixFlip
      ? acquisition.analytics.fixFlip.pointsAmountFixFlip
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.fixFlip
      .closeCostDollarFixFlip
      ? acquisition.analytics.fixFlip.closeCostDollarFixFlip
      : 0
    const closeCostPercent = acquisition.analytics.fixFlip
      .closeCostPercentFixFlip
      ? acquisition.analytics.fixFlip.closeCostPercentFixFlip
      : 0
    const paydownMonthly = acquisition.analytics.fixFlip
      .paydownMonthlyFixFlip
      ? acquisition.analytics.fixFlip.paydownMonthlyFixFlip
      : 0
    const saleProceeds = acquisition.analytics.fixFlip.saleProceeds
      ? acquisition.analytics.fixFlip.saleProceeds
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const acquisitionCommissionPercent = acquisition.analytics
      .acquisitionCommissionPercent
      ? acquisition.analytics.acquisitionCommissionPercent
      : 0
    const financingFeeTotal = acquisition.analytics.fixFlip
      .financingFeeTotalFixFlip
      ? acquisition.analytics.fixFlip.financingFeeTotalFixFlip
      : 0
    const sellingClosingCost = acquisition.analytics.fixFlip
      .sellingClosingCostDollarFixFlip
      ? acquisition.analytics.fixFlip.sellingClosingCostDollarFixFlip
      : 0
    const sellingClosingCostPercent = acquisition.analytics.fixFlip
      .sellingClosingCostPercentFixFlip
      ? acquisition.analytics.fixFlip.sellingClosingCostPercentFixFlip
      : 0
    const dispositionFee = acquisition.analytics.fixFlip
      .dispositionFeeDollarFixFlip
      ? acquisition.analytics.fixFlip.dispositionFeeDollarFixFlip
      : 0
    const salesCommission = acquisition.analytics.fixFlip
      .salesCommissionTotalFixFlip
      ? acquisition.analytics.fixFlip.salesCommissionTotalFixFlip
      : 0
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageFixFlip
      ? acquisition.analysis.financingRepairsPercentageFixFlip
      : 0
    const rehabCost = acquisition.analytics.fixFlip.corTotalFixFlip
      ? acquisition.analytics.fixFlip.corTotalFixFlip
      : 0
    const salePrice = acquisition.analysis.resellPriceFixFlip
      ? acquisition.analysis.resellPriceFixFlip
      : acquisition.analytics.fixFlip.arvTotalFixFlip || 0
    const downPayment = assumptions.downPaymentFixFlip
      ? assumptions.downPaymentFixFlip
      : 0
    const rate = assumptions.rateFixFlip ? assumptions.rateFixFlip : 0
    const term = assumptions.termFixFlip ? assumptions.termFixFlip : 0
    const termType = assumptions.termFixFlipType
      ? assumptions.termFixFlipType
      : 'Months'
    const pointPercentage = assumptions.pointsFixFlip
      ? assumptions.pointsFixFlip
      : 0
    const homePriceAppreciation = assumptions.homePriceAppreciation
      ? assumptions.homePriceAppreciation
      : 0
    const payDownAccelerator = assumptions.payDownAccelerator
      ? assumptions.payDownAccelerator
      : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const holdingPeriod = assumptions.holdingPeriod
      ? assumptions.holdingPeriod
      : 0
    const financingFee = acquisition.analytics.fixFlip
      .financingFeePercentFixFlip
      ? acquisition.analytics.fixFlip.financingFeePercentFixFlip
      : 0
    const salesCommissionPercent = assumptions.salesCommissionFixFlip
      ? assumptions.salesCommissionFixFlip
      : 0

    const homeInsurance = acquisition.analytics.fixFlip
      .propertyInsuranceDollarFixFlip
      ? acquisition.analytics.fixFlip.propertyInsuranceDollarFixFlip
      : 0
    const maintenanceDollar = acquisition.analytics.fixFlip
      .maintenanceFixFlipDollar
      ? acquisition.analytics.fixFlip.maintenanceFixFlipDollar
      : 0
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.fixFlip.hoaTotal
      ? acquisition.analytics.fixFlip.hoaTotal / 12
      : 0
    const utilities = acquisition.analytics.fixFlip
      .utilitiesTypeDollarFixFlip
      ? acquisition.analytics.fixFlip.utilitiesTypeDollarFixFlip
      : 0
    const otherExpenses = assumptions.otherExpenseFixFlip
      ? assumptions.otherExpenseFixFlip
      : 0
    const propertyManagementDollar = acquisition.analytics.fixFlip
      .propertyManagementDollarFixFlip
      ? acquisition.analytics.fixFlip.propertyManagementDollarFixFlip
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const monthlyReservesDollar = acquisition.analytics.fixFlip
      .monthlyReservesTypeDollarFixFlip
      ? acquisition.analytics.fixFlip.monthlyReservesTypeDollarFixFlip
      : 0

    const monthlyRate = rate / 12 / 100
    const financedRenovationAmount =
      rehabCost - rehabCost * (downPayment / 100) || 0
    const renovationAmount = rehabCost || 0

    const loanBalanceAtHoldingPeriod = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: holdingPeriod,
      paydownMonthly,
      holdingPeriod: true,
    })

    const monthlyHoldingExpenses =
      homeInsurance / 12 +
      propertyManagementDollar / 12 +
      monthlyHOA +
      propertyTaxes / 12 +
      maintenanceDollar / 12 +
      utilities +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12 +
      monthlyReservesDollar +
      mortgagePayment

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      sellingClosingCost +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal +
      monthlyHoldingExpenses * holdingPeriod

    const graphDataURL = getFixFlipChartData(property)

    return [
      {
        width: 400,
        stack: [
          {
            text: [
              'Financial ',
              { text: 'Projections', color: '#3399ff' },
            ],
            fontSize: 18,
            margin: [15, 20, 0, 0],
          },
          {
            canvas: [
              {
                type: 'line',
                x1: 15,
                y1: 5,
                x2: 150,
                y2: 5,
                lineWidth: 2,
              },
            ],
          },
          {
            image: graphDataURL,
            width: 400,
            height: 275,
            margin: [10, 20, 0, 0],
          },
        ],
      },
      [
        {
          margin: [20, 30],
          stack: [
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: [
                  [
                    {
                      text: 'Financing Assumptions',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  [
                    {
                      text: 'Loan Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(financeAmount),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Financed Rehab Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(
                        (financedRenovationAmount *
                          financingRepairsPercentage) /
                          100,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Interest Rate',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: rate + '%',
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage (yrs)',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: (termType === 'Years'
                        ? term
                        : term / 12
                      ).toFixed(1),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage Payment',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(mortgagePayment),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  // [
                  //   {
                  //     text:
                  //       'Paydown Acc. (' +
                  //       payDownAccelerator +
                  //       '%)',
                  //     alignment: 'left',
                  //     fontSize: 6,
                  //   },
                  //   {
                  //     text: currencyFormat.format(
                  //       paydownMonthly,
                  //     ),
                  //     alignment: 'right',
                  //     fontSize: 6,
                  //   },
                  // ],
                ],
              },
              layout: {
                defaultBorder: false,
              },
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Initial Investment',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  downPaymentTotal
                    ? [
                        {
                          text: 'Down Payment (' + downPayment + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            downPaymentTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  creditFromSeller
                    ? [
                        {
                          text: '-Credit From Seller',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            -1 * creditFromSeller,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  renovationAmount
                    ? [
                        {
                          text: 'Rehab Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            renovationAmount *
                              (1 - financingRepairsPercentage / 100),
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionFee
                    ? [
                        {
                          text: 'Acquisition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(acquisitionFee),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionCommissionDollar
                    ? [
                        {
                          text:
                            'Acquisition Commission (' +
                            acquisitionCommissionPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            acquisitionCommissionDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  closeCostDollar
                    ? [
                        {
                          text:
                            'Est Closing Cost (' +
                            closeCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            closeCostDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  sellingClosingCost
                    ? [
                        {
                          text:
                            'Est Seller Closing Cost (' +
                            sellingClosingCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            sellingClosingCost,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  financingFeeTotal
                    ? [
                        {
                          text:
                            'Financing Fee (' + financingFee + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            financingFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  customAcquisitionExpenseTotal
                    ? [
                        {
                          text: 'Custom Expenses',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            customAcquisitionExpenseTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  pointsAmount
                    ? [
                        {
                          text: 'Points (' + pointPercentage + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(pointsAmount),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  monthlyHoldingExpenses
                    ? [
                        {
                          text: 'Holding Expenses',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            monthlyHoldingExpenses * holdingPeriod,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Total Out Of Pocket',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(
                        totalInitialInvestmentFinancing,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Net Profit',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  salePrice
                    ? [
                        {
                          text: 'Sell Price at holding Period',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(salePrice),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  salesCommission
                    ? [
                        {
                          text:
                            'Sales Commission (' +
                            (assumptions.salesCommissionFixFlipType ===
                            '$'
                              ? currencyFormat.format(
                                  salesCommissionPercent,
                                ) + ')'
                              : salesCommissionPercent + '%)'),
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            salesCommission,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  dispositionFee
                    ? [
                        {
                          text: 'Disposition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(dispositionFee),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  totalInitialInvestmentFinancing
                    ? [
                        {
                          text: 'Total Out Of Pocket',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  loanBalanceAtHoldingPeriod
                    ? [
                        {
                          text: 'Loan Balance',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            loanBalanceAtHoldingPeriod,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Net Profit',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(saleProceeds),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
          ],
        },
      ],
    ]
  }

  /**
   * Generate page 2 bottom section
   */
  async generatePage2Bottom() {
    const { property } = this

    const {
      acquisition = {},
      assumptions = {},
      portfolio = {},
    } = property
    const { organization = {} } = portfolio
    const { legalDescription = '' } = organization
    const disclaimerNode = buildProformaDisclaimerPdfNode(legalDescription)

    const mortgagePayment = acquisition.analytics.fixFlip
      .mortgageFixFlip
      ? acquisition.analytics.fixFlip.mortgageFixFlip
      : 0
    const financeAmount = acquisition.analytics.fixFlip
      .loanAmountFixFlip
      ? acquisition.analytics.fixFlip.loanAmountFixFlip
      : 0
    const paydownMonthly = acquisition.analytics.fixFlip
      .paydownMonthlyFixFlip
      ? acquisition.analytics.fixFlip.paydownMonthlyFixFlip
      : 0
    const downPaymentTotal = acquisition.analytics.fixFlip
      .downPaymentTotalFixFlip
      ? acquisition.analytics.fixFlip.downPaymentTotalFixFlip
      : 0
    const pointsAmount = acquisition.analytics.fixFlip
      .pointsAmountFixFlip
      ? acquisition.analytics.fixFlip.pointsAmountFixFlip
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.fixFlip
      .closeCostDollarFixFlip
      ? acquisition.analytics.fixFlip.closeCostDollarFixFlip
      : 0
    const closeCostPercent = acquisition.analytics.fixFlip
      .closeCostPercentFixFlip
      ? acquisition.analytics.fixFlip.sellingClosingCostPercentFixFlip
      : 0
    const sellerCloseCostDollar = acquisition.analytics.fixFlip
      .sellingClosingCostDollarFixFlip
      ? acquisition.analytics.fixFlip.sellingClosingCostDollarFixFlip
      : 0
    const sellerCloseCostPercent = acquisition.analytics.fixFlip
      .sellingClosingCostPercentFixFlip
      ? acquisition.analytics.fixFlip.sellingClosingCostPercentFixFlip
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const acquisitionCommissionPercent = acquisition.analytics
      .acquisitionCommissionPercent
      ? acquisition.analytics.acquisitionCommissionPercent
      : 0
    const propertyManagementDollar = acquisition.analytics.fixFlip
      .propertyManagementDollarFixFlip
      ? acquisition.analytics.fixFlip.propertyManagementDollarFixFlip
      : 0
    const utilities = acquisition.analytics.fixFlip
      .utilitiesTypeDollarFixFlip
      ? acquisition.analytics.fixFlip.utilitiesTypeDollarFixFlip
      : 0
    const utilitiesPercent = assumptions.utilitiesFixFlip
      ? assumptions.utilitiesFixFlip
      : 0
    const financingFeeTotal = acquisition.analytics.fixFlip
      .financingFeeTotalFixFlip
      ? acquisition.analytics.fixFlip.financingFeeTotalFixFlip
      : 0
    const assetManagementFeeTotal = acquisition.analytics.fixFlip
      .assetManagementFeeTotalFixFlip
      ? acquisition.analytics.fixFlip.assetManagementFeeTotalFixFlip
      : 0
    const resalePrice = acquisition.analysis.resellPriceFixFlip
      ? acquisition.analysis.resellPriceFixFlip
      : acquisition.analytics.fixFlip.arvTotalFixFlip || 0

    const homeInsurance = acquisition.analytics.fixFlip
      .propertyInsuranceDollarFixFlip
      ? acquisition.analytics.fixFlip.propertyInsuranceDollarFixFlip
      : 0
    const maintenanceDollar = acquisition.analytics.fixFlip
      .maintenanceFixFlipDollar
      ? acquisition.analytics.fixFlip.maintenanceFixFlipDollar
      : 0

    const dispositionFee = acquisition.analytics.fixFlip
      .dispositionFeeDollarFixFlip
      ? acquisition.analytics.fixFlip.dispositionFeeDollarFixFlip
      : 0
    const salesCommission = acquisition.analytics.fixFlip
      .salesCommissionTotalFixFlip
      ? acquisition.analytics.fixFlip.salesCommissionTotalFixFlip
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.fixFlip.hoaTotal
      ? acquisition.analytics.fixFlip.hoaTotal / 12
      : 0
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageFixFlip
      ? acquisition.analysis.financingRepairsPercentageFixFlip
      : 0
    // const rehabCost = acquisition.analysis.rehabCost
    //   ? acquisition.analysis.rehabCost
    //   : 0
    const rehabCost = acquisition.analytics.fixFlip.corTotalFixFlip
      ? acquisition.analytics.fixFlip.corTotalFixFlip
      : 0
    const downPayment = assumptions.downPaymentFixFlip
      ? assumptions.downPaymentFixFlip
      : 0
    const rate = assumptions.rateFixFlip ? assumptions.rateFixFlip : 0
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceFixFlip
      ? assumptions.maintenanceFixFlip
      : 0
    const pointPercentage = assumptions.pointsFixFlip
      ? assumptions.pointsFixFlip
      : 0
    const financingFee = acquisition.analytics.fixFlip
      .financingFeePercentFixFlip
      ? acquisition.analytics.fixFlip.financingFeePercentFixFlip
      : 0
    const propertyMgmtPercent = acquisition.analytics.fixFlip
      .propertyManagementPercentageFixFlip
      ? acquisition.analytics.fixFlip
          .propertyManagementPercentageFixFlip
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const holdingPeriod = assumptions.holdingPeriod
      ? assumptions.holdingPeriod
      : 0

    const otherExpenses = assumptions.otherExpenseFixFlip
      ? assumptions.otherExpenseFixFlip
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const monthlyReservesDollar = acquisition.analytics.fixFlip
      .monthlyReservesTypeDollarFixFlip
      ? acquisition.analytics.fixFlip.monthlyReservesTypeDollarFixFlip
      : 0

    const renovationAmount = rehabCost || 0

    const acquisitionExpenses =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal

    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementFixFlipType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementFixFlipType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementFixFlipType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesFixFlipType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesFixFlipType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesFixFlipType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const NOINoFinance1 = grossRentalIncome1 - totalExpense1
    const NOINoFinance3 = grossRentalIncome3 - totalExpense3
    const NOINoFinance5 = grossRentalIncome5 - totalExpense5
    const NOINoFinance10 = grossRentalIncome10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })

    const monthlyRate = rate / 12 / 100

    const loanBalanceAtHoldingPeriod = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: holdingPeriod,
      paydownMonthly,
      holdingPeriod: true,
    })

    const monthlyHoldingExpenses =
      homeInsurance / 12 +
      propertyManagementDollar / 12 +
      monthlyHOA +
      propertyTaxes / 12 +
      maintenanceDollar / 12 +
      utilities +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12 +
      monthlyReservesDollar +
      mortgagePayment

    const dispositionExpenses =
      loanBalanceAtHoldingPeriod +
      sellerCloseCostDollar +
      salesCommission +
      dispositionFee

    const netProfit =
      resalePrice -
      acquisitionExpenses -
      monthlyHoldingExpenses * holdingPeriod -
      dispositionExpenses

    return [
      {
        margin: [15, 0, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*'],
          body: compact([
            [
              {
                text: 'Acquisition Expenses',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 2,
                alignment: 'left',
              },
              { text: '' },
            ],
            downPaymentTotal
              ? [
                  {
                    text: 'Down Payment (' + downPayment + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(downPaymentTotal),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            acquisitionFee
              ? [
                  {
                    text: 'Acquisition Fee',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(acquisitionFee),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            acquisitionCommissionDollar
              ? [
                  {
                    text:
                      'Acquisition Commission (' +
                      acquisitionCommissionPercent +
                      '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      acquisitionCommissionDollar,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            closeCostDollar
              ? [
                  {
                    text:
                      'Est Closing Cost (' + closeCostPercent + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(closeCostDollar),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            financingFeeTotal
              ? [
                  {
                    text: 'Financing Fee (' + financingFee + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(financingFeeTotal),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            customAcquisitionExpenseTotal
              ? [
                  {
                    text: 'Custom Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAcquisitionExpenseTotal,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            pointsAmount
              ? [
                  {
                    text: 'Points (' + pointPercentage + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(pointsAmount),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            creditFromSeller
              ? [
                  {
                    text: '-Credit From Seller',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      -1 * creditFromSeller,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            renovationAmount
              ? [
                  {
                    text: 'Rehab Amount',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      renovationAmount *
                        (1 - financingRepairsPercentage / 100),
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Acquisition Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(acquisitionExpenses),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*'],
          body: compact([
            [
              {
                text: `Holding Expenses (${holdingPeriod} months)`,
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                alignment: 'left',
              },
              {
                text: 'Monthly Amount',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                alignment: 'right',
              },
              {
                text: `${holdingPeriod} Month Total`,
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                alignment: 'right',
              },
            ],
            propertyTaxes
              ? [
                  {
                    text: 'Monthly Property Taxes',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (propertyTaxes / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            homeInsurance
              ? [
                  {
                    text: 'Monthly Homeowners Insurance',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (homeInsurance / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            propertyManagementDollar
              ? [
                  {
                    text: 'Property Management',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      propertyManagementDollar / 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (propertyManagementDollar / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            monthlyHOA
              ? [
                  {
                    text: 'Monthly HOA Fees',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(monthlyHOA),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      monthlyHOA * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            maintenanceDollar
              ? [
                  {
                    text: 'Monthly Maintenance Cost',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      maintenanceDollar / 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (maintenanceDollar / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            utilities
              ? [
                  {
                    text: 'Monthly Utilities',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      utilities * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            monthlyReservesDollar
              ? [
                  {
                    text: 'Monthly Owner Reserves',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      monthlyReservesDollar,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      monthlyReservesDollar * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            otherExpenses
              ? [
                  {
                    text: 'Other Monthly Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpenses / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (otherExpenses / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            customAnnualExpenseTotal
              ? [
                  {
                    text: 'Custom Monthly Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal / 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (customAnnualExpenseTotal / 12) * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            mortgagePayment
              ? [
                  {
                    text: 'Mortgage',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(mortgagePayment),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      mortgagePayment * holdingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Holding Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(monthlyHoldingExpenses),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(
                  monthlyHoldingExpenses * holdingPeriod,
                ),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*'],
          body: compact([
            [
              {
                text: 'Disposition Expenses',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 2,
                alignment: 'left',
              },
              { text: '' },
            ],
            loanBalanceAtHoldingPeriod
              ? [
                  {
                    text: 'Loan Balance at Resale',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      loanBalanceAtHoldingPeriod,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            sellerCloseCostDollar
              ? [
                  {
                    text:
                      'Est Closing Cost (' +
                      sellerCloseCostPercent +
                      '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sellerCloseCostDollar,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            salesCommission
              ? [
                  {
                    text: 'Sales Commission',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(salesCommission),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            dispositionFee
              ? [
                  {
                    text: 'Disposition Fee',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(dispositionFee),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Disposition Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(dispositionExpenses),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*'],
          body: [
            [
              {
                text: 'Net Profit',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 2,
                alignment: 'left',
              },
              { text: '' },
            ],
            [
              {
                text: 'Resell Price',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(resalePrice),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Acquisition Expenses',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(acquisitionExpenses),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Holding Expenses',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(
                  monthlyHoldingExpenses * holdingPeriod,
                ),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Disposition Expenses',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(dispositionExpenses),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Net Profit',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(netProfit),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      ...(disclaimerNode ? [disclaimerNode] : []),
    ]
  }

}
